import React, { useCallback, useEffect, useState } from 'react';
import debounce from 'lodash.debounce';
import DropdownTrigger from '@salesforce/design-system-react/module/components/menu-dropdown/button-trigger';
import DataTable from '@salesforce/design-system-react/module/components/data-table';
import DataTableColumn from '@salesforce/design-system-react/module/components/data-table/column';
import DataTableCell from '@salesforce/design-system-react/module/components/data-table/cell';
import Icon from '@salesforce/design-system-react/module/components/icon';
import PageHeader from '@salesforce/design-system-react/module/components/page-header';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonGroup, Dropdown, PageHeaderControl } from '@salesforce/design-system-react';
import Input from '@salesforce/design-system-react/components/input';
import InputIcon from '@salesforce/design-system-react/components/icon/input-icon';
import IconSettings from '@salesforce/design-system-react/components/icon-settings';
import Tooltip from '@salesforce/design-system-react/components/tooltip';
import { isEmpty } from '@firebase/util';
import { toast, Toaster } from 'react-hot-toast';
import { getTimeIntervalText } from '../../utilities/datetime';
import EditSubscriptionPlan from '../../components/Modal/editSubscriptionPlan';
import {
  getSubscriptionPlan,
  subscriptionPlansSelector,
} from '../../store/subscriptionPlans/slice';

const CustomWalletCell = ({ children, ...props }) => {
  return (
    <DataTableCell title={children} {...props}>
      <div style={{ fontWeight: 'bold', color: '#080707' }}>{children}</div>
    </DataTableCell>
  );
};

CustomWalletCell.displayName = DataTableCell.displayName;

const CustomDataTablePrice = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell title={children} {...props}>
      {item.price} $
    </DataTableCell>
  );
};
CustomDataTablePrice.displayName = DataTableCell.displayName;

const CustomDataTableDate = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell title={children} {...props}>
      {item.syncRepetation} {item.syncRepetation === 1 ? 'time' : 'times'}{' '}
      {item.syncPeriod === 'HOUR' ? 'an' : 'a'} {item.syncPeriod}
    </DataTableCell>
  );
};
CustomDataTableDate.displayName = DataTableCell.displayName;

const CustomDataTableExpiresDay = ({ children, ...props }) => {
  const { item } = props;

  return (
    <DataTableCell title={children} {...props}>
      {item.expiresInDays} days
    </DataTableCell>
  );
};
CustomDataTableExpiresDay.displayName = DataTableCell.displayName;

const CustomActionTableDate = ({ children, ...props }) => {
  const { item } = props;

  const openEditSubscriptionPlan = () => {
    props.setEditSubscriptionPlanModal(true);
    props.setUser(item);
  };

  return (
    <DataTableCell title={'Actions'} {...props}>
      <Button
        className="CustomButtonLarge action-pause-resume-button"
        key="Edit"
        label="Edit"
        responsive
        variant="neutral"
        title="Edit plan"
        onClick={openEditSubscriptionPlan}
      />
    </DataTableCell>
  );
};
CustomActionTableDate.displayName = DataTableCell.displayName;

const SubscriptionPlan = () => {
  const dispatch = useDispatch();
  // const navigate = useNavigate();
  const { loading, pagination, subscriptionPlans, errors, success, message } =
    useSelector(subscriptionPlansSelector);
  const [showToast, setShowToast] = useState(false);
  const [sortColumn, setSortColumn] = useState('');
  const [sortColumnDirection, setSortColumnDirection] = useState('');
  const [items, setItems] = useState([]);
  const [paginated, setPaginated] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  // const [addSubscriptionPlanModal, setAddSubscriptionPlanModal] = useState(false);
  const [editSubscriptionPlanModal, setEditSubscriptionPlanModal] = useState(false);
  const [APICalledTime, setAPICalledTime] = useState(new Date());
  const [actionSubscriptionPlanModal, setActionSubscriptionPlanModal] = useState({
    open: false,
    type: '',
  });
  const [text, setText] = useState('Recently updated');
  const [searchText, setSearchText] = useState('');
  const [plan, setPlan] = useState({});

  useEffect(() => {
    dispatch(getSubscriptionPlan());
  }, [dispatch]);

  useEffect(() => {
    setHasMore(true);
    if (subscriptionPlans.length > 0) setItems(subscriptionPlans);
    setAPICalledTime(new Date());
    const data = getTimeIntervalText(APICalledTime);
    setText(data);
  }, [subscriptionPlans, dispatch]);

  useEffect(() => {
    const interval = setInterval(() => {
      const data = getTimeIntervalText(APICalledTime);
      setText(data);
    }, 20000);

    return () => clearInterval(interval);
  }, [APICalledTime]);

  useEffect(() => {
    if (!showToast) return;
    success &&
      !loading &&
      toast.success(message, {
        id: 'subscription-add-plan',
        duration: 3000,
      });
    // setShowToast(false)

    !success &&
      !loading &&
      !isEmpty(errors) &&
      toast.error(errors, {
        id: 'standardplan-error',
        duration: 3000,
      });
  }, [success, errors, message, showToast, loading]);

  const controls = () => (
    <React.Fragment>
      <PageHeaderControl>
        <IconSettings iconPath="/assets/icons">
          <Input
            iconLeft={
              <InputIcon
                assistiveText={{
                  icon: 'Search',
                }}
                name="search"
                category="utility"
              />
            }
            id="search_text"
            placeholder="Search"
            value={searchText}
            onChange={(e) => onSearchText(e.target.value)}
            fixedTextRight={
              <Tooltip
                id="search-tooltip"
                align="top right"
                content="Search works on the basis of name"
                position="overflowBoundaryElement"
              />
            }
          />
        </IconSettings>
      </PageHeaderControl>
      <PageHeaderControl>
        <Button
          assistiveText={{ icon: 'Refresh' }}
          iconCategory="utility"
          iconName="refresh"
          iconVariant="border-filled"
          variant="icon"
          onClick={() => {
            refreshTable();
          }}
        />
      </PageHeaderControl>
    </React.Fragment>
  );

  const handleSort = (sortColumn, ...rest) => {
    const { property, sortDirection } = sortColumn;
    setSortColumn(property);
    setSortColumnDirection(sortDirection);
    setItems([]);
    setHasMore(true);
    setIsPageLoading(false);
    setPaginated(0);
    dispatch(getSubscriptionPlan({ sort_column: property, sort_order: sortDirection }));
  };

  const handleLoadMore = () => {
    if (!loading && !isPageLoading) {
      if (pagination?.hasNextPage) {
        setIsPageLoading(true);
        setPaginated(paginated + 1);
        setTimeout(() => {
          if (pagination.pages <= 1 || paginated >= pagination.pages || !pagination.hasNextPage) {
            setHasMore(false);
            setIsPageLoading(false);
            return;
          }

          if (pagination.currentPage >= pagination.pages) {
            setHasMore(false);
            setIsPageLoading(false);
            return;
          }
          let url = new URLSearchParams(pagination.nextPageUrl);

          dispatch(
            getSubscriptionPlan({
              limit: url.get('limit'),
              offset: url.get('offset'),
              q: searchText,
              sort_column: sortColumn,
              sort_order: sortColumnDirection,
            })
          );
          setIsPageLoading(false);
        }, 1000);
      } else {
        setIsPageLoading(false);
        setHasMore(false);
      }
    }
  };

  const refreshTable = () => {
    setHasMore(true);
    setPaginated(0);
    setItems([]);
    if (searchText !== '') {
      searchHandler(searchText);
    } else {
      dispatch(getSubscriptionPlan());
    }
  };

  const searchHandler = useCallback(
    debounce((values) => {
      setHasMore(true);
      setItems([]);
      setPaginated(0);
      dispatch(
        getSubscriptionPlan({
          q: values,
        })
      ).then((res) => setItems(res?.payload?.data));
      values === '' && dispatch(getSubscriptionPlan());
    }, 1000),
    []
  );

  const onSearchText = (value) => {
    setSearchText(value);
    searchHandler(value);
  };

  return (
    <>
      <div className="pageContent">
        <Toaster position="top-right" reverseOrder={false} />
        <PageHeader
          icon={<Icon assistiveText={{ label: 'Accounts' }} category="standard" name="account" />}
          info={`${items ? items.length : 0} items • ${text}`}
          joined
          btnLabel="Add plan"
          label="Plans"
          title={
            <h1 className="slds-page-header__title slds-p-right_x-small">
              <Dropdown id="page-header-dropdown-object-home-header">
                <DropdownTrigger>
                  <Button
                    className="slds-button_reset slds-type-focus"
                    iconCategory="utility"
                    iconName="down"
                    iconPosition="right"
                    label="All plans"
                    responsive
                    variant="base"
                  />
                </DropdownTrigger>
              </Dropdown>
            </h1>
          }
          onRenderActions={() => (
            <>
              <PageHeaderControl>
                <ButtonGroup id="button-group-page-header-actions">
                  {/* <Button
                    onClick={() => setAddSubscriptionPlanModal(true)}
                    label={'Add Subscription Plan'}
                  /> */}
                </ButtonGroup>
              </PageHeaderControl>
            </>
          )}
          onRenderControls={controls}
          truncate
          variant="object-home"
        />

        <div
          style={{ height: `${subscriptionPlans.length === 0 ? '100%' : 'calc(100vh - 220px)'}` }}
        >
          <DataTable
            assistiveText={{
              actionsHeader: 'actions',
              columnSort: 'sort this column',
              columnSortedAscending: 'asc',
              columnSortedDescending: 'desc',
              selectAllRows: 'Select all rows',
              selectRow: 'Select this row',
            }}
            fixedHeader
            fixedLayout
            items={items}
            id="Assets-Table"
            onSort={handleSort}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            noRowHover
          >
            <DataTableColumn
              isSorted={sortColumn === 'name'}
              label="Name"
              property="name"
              sortable
              sortDirection={sortColumnDirection.name}
            >
              <CustomWalletCell
                setEditSubscriptionPlanModal={setEditSubscriptionPlanModal}
                setUser={setPlan}
              />
            </DataTableColumn>
            <DataTableColumn
              isSorted={sortColumn === 'price'}
              label="Price"
              property="price"
              sortable
              sortDirection={sortColumnDirection.price}
            >
              <CustomDataTablePrice />
            </DataTableColumn>
            <DataTableColumn label="Sync time" property="syncTime">
              <CustomDataTableDate />
            </DataTableColumn>
            <DataTableColumn
              isSorted={sortColumn === 'expiresInDays'}
              label="Expiry time"
              property="expiresInDays"
              sortable
              sortDirection={sortColumnDirection.duration}
            >
              <CustomDataTableExpiresDay />
            </DataTableColumn>
            <DataTableColumn label="Action" width="10rem">
              <CustomActionTableDate
                setEditSubscriptionPlanModal={setEditSubscriptionPlanModal}
                setUser={setPlan}
              />
            </DataTableColumn>
          </DataTable>

          {subscriptionPlans.length === 0 && !hasMore && (
            <div className="nodatafound">
              <div>
                <img
                  src="/assets/empty-state/empty-folder.png"
                  alt=""
                  style={{ height: '150px' }}
                />
                <p style={{ textAlign: 'center', fontWeight: '700', fontSize: '20px' }}>
                  Data not available
                </p>
              </div>
            </div>
          )}
        </div>

        <EditSubscriptionPlan
          openModal={editSubscriptionPlanModal}
          closeModal={() => setEditSubscriptionPlanModal(false)}
          plan={plan}
          type="standard"
        />
      </div>
    </>
  );
};

export default SubscriptionPlan;
